import { PresenceMember } from "@ably/chat";
import { Image, Text, Flex, Box, Divider } from "@chakra-ui/react";
import { useIsSmallBreakpoint } from "../../../utils/useBreakpoints";
import { GCSmallLogoIcon } from "../../icons/GCSmallLogoIcon";
import { Avatar } from "../../user/Avatar";
import { UserChatProps } from "../types";
import { useMemo } from "react";
import { useChatContext } from "../hooks/useChat";
import { useAccountData } from "../../../hooks/useAccountData";

export function ChatHeader({
  compact = false,
  title = "Chat",
  icon,
  button,
}: {
  compact?: boolean;
  title?: string;
  icon?: string | JSX.Element;
  button?: JSX.Element;
}) {
  const { members } = useChatContext();
  const isSmallScreen = useIsSmallBreakpoint();
  return (
    <Flex direction="column" gap="8px" width="100%">
      <Flex
        maxWidth="100%"
        justifyContent="flex-start"
        align="center"
        py={compact ? "8px" : "16px"}
        px={isSmallScreen || !compact ? "16px" : 0}
      >
        {!compact &&
          (typeof icon === "string" ? (
            <Image
              src={icon}
              alt={title}
              height={isSmallScreen ? "56px" : "42px"}
              width={isSmallScreen ? "56px" : "42px"}
              borderRadius="30%"
            />
          ) : (
            <GCSmallLogoIcon
              color="blaze.blaze"
              boxSize={isSmallScreen ? "56px" : "42px"}
            />
          ))}

        <Flex
          flex="1"
          direction={isSmallScreen ? "column" : "row"}
          align={isSmallScreen ? "flex-start" : "space-between"}
          justify={isSmallScreen ? "flex-start" : "space-between"}
          gap={compact ? "8px" : "12px"}
          w="100%"
          paddingX="20px"
          paddingLeft={compact ? "0" : "20px"}
          minW="0"
          flexShrink={0}
        >
          <Text
            fontWeight="700"
            fontSize={compact || isSmallScreen ? "20px" : "32px"}
            color="dune.dune"
            fontFamily="Bricolage Grotesque"
            overflow={isSmallScreen ? "visible" : "hidden"}
            textOverflow={isSmallScreen ? "visible" : "ellipsis"}
            whiteSpace={isSmallScreen ? "normal" : "nowrap"}
            title={title}
          >
            {title}
          </Text>
          <Flex
            flex="1"
            justify="flex-end"
            ml={!compact && isSmallScreen ? "8px" : undefined}
          >
            <PresentMemberAvatars members={members} />
          </Flex>
        </Flex>

        {button}
      </Flex>

      <Divider color="transparent.white.20" />
    </Flex>
  );
}

function PresentMemberAvatars({
  members,
}: {
  members: Record<string, PresenceMember>;
}) {
  const accountData = useAccountData();
  const isUserProfileEnabled = accountData?.features?.["user_profile"];
  // Filter out members whose data.state is 'leave' before processing avatars
  const memberList = Object.values(members).filter(
    (member) => member.action !== "leave",
  );

  const maxVisible = 8;

  // Loop through members until we have maxVisible avatars
  // or we run out of members to loop through
  const visibleMembersArr: PresenceMember[] = useMemo(() => {
    const visibleMembers: Record<string, PresenceMember> = {};
    let i = 0;
    while (
      Object.keys(visibleMembers).length < maxVisible &&
      memberList.length > 0 &&
      memberList[i]
    ) {
      const memberData = memberList[i].data as UserChatProps;
      if (memberData?.avatar) {
        visibleMembers[memberData.avatar] = memberList[i];
      } else {
        visibleMembers[
          `${window.gc.global.assets_url}/static/images/avatars/no-avatar.png`
        ] = memberList[i];
      }
      i++;
    }
    return Object.values(visibleMembers);
  }, [members]);

  const overflowCount = memberList.length - visibleMembersArr.length;

  return (
    <Flex alignItems="center">
      {visibleMembersArr.map((member, index) => {
        const { username, avatar, isGold } = (member.data ??
          {}) as UserChatProps;

        return (
          <Box
            key={member.clientId}
            ml={index === 0 ? undefined : "-12px"}
            zIndex={visibleMembersArr.length - index}
          >
            <Avatar
              key={member.clientId}
              size="sm"
              username={username}
              imageSrc={avatar}
              isGold={isGold}
              openProfileOnClick={!!!isUserProfileEnabled}
            />
          </Box>
        );
      })}

      {overflowCount > 0 && (
        <Box
          ml="-8px"
          boxSize="40px"
          display="flex"
          color="dune.700"
          alignItems="center"
          justifyContent="center"
          fontWeight="bold"
        >
          +{overflowCount}
        </Box>
      )}
    </Flex>
  );
}
